import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { EndpointEditor } from './EndpointEditor';
import { InboundConfig } from './InboundConfig';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

export interface ChannelRow {
  id: string;
  callbackUrl: string | null;
  inbound: {
    statusPath?: string;
    eventIdPath?: string;
    channelOrderNoPath?: string;
    orderRefPath?: string;
    map?: Record<string, { action: string }>;
    verify?: { type?: string; signHeader?: string; timestampHeader?: string };
  } | null;
  code: string;
  name: string;
  kind: string;
  baseUrl: string;
  environment: string;
  authType: string;
  credential: { ref: string | null; configured: boolean; hint: string };
  isActive: boolean;
  operations: string[];
  notifyTrigger: { on?: string[]; customerCodes?: string[] } | null;
}

interface EndpointRow {
  operation: string;
  method: string;
  path: string;
  contentType: string;
  requestTemplate: string | null;
  successCondition: string | null;
  responseMapping: { extract?: Record<string, string> };
  isActive: boolean;
}

const AUTH_LABEL: Record<string, string> = {
  NONE: '不认证',
  API_KEY: 'API Key',
  BEARER: 'Bearer Token',
  BASIC: 'Basic',
  HMAC: 'HMAC 签名',
  CUSTOM_HEADER: '自定义头',
  OAUTH2_CLIENT_CREDENTIALS: 'OAuth2',
};

/**
 * 三方通道配置。
 *
 * ★ 一条通道能不能真的用起来,取决于三件事同时就位:
 *   启用了、凭据配上了、映射配好了。
 *   缺任何一件都会在推单那一刻才炸,而那时候单子已经积了一批。
 *   所以列表上把这三件事**分别**标出来,而不是一个「已配置」了事。
 */
export function ChannelSettings({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<ChannelRow[]>([]);
  const [editing, setEditing] = useState<ChannelRow | 'new' | null>(null);
  const [mapping, setMapping] = useState<{
    ch: ChannelRow;
    op: string;
    ep?: EndpointRow;
  } | null>(null);
  const [inbound, setInbound] = useState<ChannelRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api<ChannelRow[]>('/integrations/channels'));
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '读不到通道列表');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function openMapping(ch: ChannelRow) {
    // 商家系统配的是 NOTIFY_STATUS(推状态),不是 CREATE_ORDER(建单)
    const op = ch.kind === 'ERP' ? 'NOTIFY_STATUS' : 'CREATE_ORDER';
    const eps = await api<EndpointRow[]>(`/integrations/channels/${ch.id}/endpoints`);
    setMapping({ ch, op, ep: eps.find((e) => e.operation === op) });
  }

  async function toggle(ch: ChannelRow) {
    try {
      await api(`/integrations/channels/${ch.id}`, {
        method: 'PATCH',
        body: { isActive: !ch.isActive },
      });
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '改不动');
    }
  }

  async function remove(ch: ChannelRow) {
    if (!confirm(`删掉通道「${ch.name}」?已经推出去的单不受影响。`)) return;
    try {
      await api(`/integrations/channels/${ch.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '删不掉');
    }
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">三方平台通道</h2>
        {canManage && (
          <button
            onClick={() => setEditing('new')}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium"
          >
            + 新建通道
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">
          还没有配三方通道。配好之后,派单时就多一个「推给第三方平台」的选择。
        </p>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto
                        bg-white dark:bg-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
              <tr>
                <th className="p-3 text-left font-medium">通道</th>
                <th className="p-3 text-left font-medium">类型</th>
                <th className="p-3 text-left font-medium">地址</th>
                <th className="p-3 text-left font-medium">认证</th>
                <th className="p-3 text-left font-medium">就绪情况</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const isErp = c.kind === 'ERP';
                // 两类通道的「配全了」不是一回事:运力平台要能建单,
                // 商家系统要能收状态 —— 检查的是不同的端点
                const op = isErp ? 'NOTIFY_STATUS' : 'CREATE_ORDER';
                const hasMapping = c.operations.includes(op);
                return (
                  <tr key={c.id} className="border-t border-slate-100 dark:border-slate-700/50">
                    <td className="p-3">
                      <div className="text-slate-900 dark:text-slate-100">{c.name}</div>
                      <div className="text-xs text-slate-400 font-mono">{c.code}</div>
                    </td>
                    <td className="p-3 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                      {c.kind === 'ERP' ? '商家系统' : '运力平台'}
                    </td>
                    <td className="p-3 text-xs font-mono text-slate-600 dark:text-slate-400">
                      <div className="truncate max-w-[220px]">{c.baseUrl}</div>
                      {c.environment === 'SANDBOX' && (
                        <span className="text-amber-600 dark:text-amber-400">沙箱</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-600 dark:text-slate-400">
                      {AUTH_LABEL[c.authType] ?? c.authType}
                    </td>
                    {/* ★ 三件事分别标出来。合成一个「已配置」的话,
                           推单那一刻才发现缺哪件 */}
                    <td className="p-3">
                      <div className="flex flex-col gap-0.5 text-xs">
                        <Ready ok={c.isActive} okText="已启用" noText="未启用" />
                        <Ready
                          ok={c.authType === 'NONE' || c.credential.configured}
                          okText="凭据已配"
                          noText="凭据未配"
                        />
                        <Ready ok={hasMapping} okText="映射已配" noText="映射未配" />
                        {isErp ? (
                          // 商家系统不需要回调,它需要的是「什么时候推」
                          <Ready
                            ok={Boolean(c.notifyTrigger?.on?.length)}
                            okText={`订阅 ${c.notifyTrigger?.on?.length ?? 0} 个状态`}
                            noText="没订阅任何状态"
                          />
                        ) : (
                          // 回调没配的话,单子推出去就断了线 —— 也要标出来
                          <Ready
                            ok={Boolean(c.inbound?.statusPath)}
                            okText="回调已配"
                            noText="回调未配"
                          />
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {canManage && (
                        <>
                          <button
                            onClick={() => void openMapping(c)}
                            className="text-brand-600 dark:text-brand-500 hover:underline mr-3"
                          >
                            字段映射
                          </button>
                          {/* 商家系统是单向的:我们推给它,它不回调 */}
                          {!isErp && (
                            <button
                              onClick={() => setInbound(c)}
                              className="text-brand-600 dark:text-brand-500 hover:underline mr-3"
                            >
                              回调配置
                            </button>
                          )}
                          <button
                            onClick={() => setEditing(c)}
                            className="text-brand-600 dark:text-brand-500 hover:underline mr-3"
                          >
                            编辑
                          </button>
                          <button
                            onClick={() => void toggle(c)}
                            className="text-slate-500 hover:underline mr-3"
                          >
                            {c.isActive ? '停用' : '启用'}
                          </button>
                          <button
                            onClick={() => void remove(c)}
                            className="text-red-600 dark:text-red-400 hover:underline"
                          >
                            删除
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ChannelForm
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      {inbound && (
        <InboundConfig
          channelId={inbound.id}
          channelName={inbound.name}
          callbackUrl={inbound.callbackUrl}
          existing={inbound.inbound}
          credentialHint={
            inbound.credential.configured ? undefined : inbound.credential.hint
          }
          onClose={() => setInbound(null)}
          onSaved={load}
        />
      )}

      {mapping && (
        <EndpointEditor
          channelId={mapping.ch.id}
          channelName={mapping.ch.name}
          operation={mapping.op}
          existing={mapping.ep}
          onClose={() => setMapping(null)}
          onSaved={load}
        />
      )}
    </section>
  );
}

function Ready({ ok, okText, noText }: { ok: boolean; okText: string; noText: string }) {
  return (
    <span className={ok ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}>
      {ok ? '✓' : '✗'} {ok ? okText : noText}
    </span>
  );
}

/** 通道本身的配置。凭据这里只填**引用**,不填值 */
function ChannelForm({
  existing,
  onClose,
  onSaved,
}: {
  existing?: ChannelRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    kind: existing?.kind ?? 'COURIER',
    baseUrl: existing?.baseUrl ?? 'https://',
    environment: existing?.environment ?? 'SANDBOX',
    authType: existing?.authType ?? 'NONE',
    credentialRef: existing?.credential.ref ?? '',
    constants: '{}',
    config: '{}',
  });
  const [notifyOn, setNotifyOn] = useState<string[]>(existing?.notifyTrigger?.on ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      let constants: unknown;
      let config: unknown;
      try {
        constants = JSON.parse(form.constants || '{}');
        config = JSON.parse(form.config || '{}');
      } catch {
        throw new Error('常量或通道配置不是合法的 JSON');
      }
      const body = {
        name: form.name,
        kind: form.kind,
        ...(form.kind === 'ERP'
          ? { notifyTrigger: { on: notifyOn, customerCodes: [] } }
          : {}),
        baseUrl: form.baseUrl,
        environment: form.environment,
        authType: form.authType,
        credentialRef: form.credentialRef.trim() || undefined,
        constants,
        authConfig: { config },
      };
      if (existing) {
        await api(`/integrations/channels/${existing.id}`, { method: 'PATCH', body });
      } else {
        await api('/integrations/channels', { method: 'POST', body: { ...body, code: form.code } });
      }
      await onSaved();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="通道配置"
        className="w-full max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {existing ? '编辑通道' : '新建通道'}
          </h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {!existing && (
          <Field label="代号" hint="小写字母、数字和连字符。建好之后不能改 —— 模板和日志里到处引用它">
            <input
              aria-label="代号"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="lalamove-my"
              className={INPUT}
            />
          </Field>
        )}

        <Field label="名称">
          <input
            aria-label="名称"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={INPUT}
          />
        </Field>

        {/*
          ★ 通道分两类,方向相反:
            COURIER —— 我们把单推给它,它送
            ERP     —— 我们把状态推给它,让下单的商家知道送到哪了
          放在最上面选,因为它决定下面要配什么
        */}
        <Field label="通道类型">
          <select
            aria-label="通道类型"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className={INPUT}
          >
            <option value="COURIER">运力平台 —— 把单推给它送</option>
            <option value="ERP">商家系统 —— 把状态推回去</option>
          </select>
        </Field>

        {form.kind === 'ERP' && (
          <Field
            label="什么时候推"
            hint="★ 一个都不勾就是什么都不推 —— 免得通道刚建好还没配完就开始往外发"
          >
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {NOTIFY_STATUSES.map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    aria-label={label}
                    checked={notifyOn.includes(value)}
                    onChange={(e) =>
                      setNotifyOn(
                        e.target.checked
                          ? [...notifyOn, value]
                          : notifyOn.filter((x) => x !== value),
                      )
                    }
                  />
                  <span className="text-slate-700 dark:text-slate-300">{label}</span>
                </label>
              ))}
            </div>
          </Field>
        )}

        <Field
          label="接口地址"
          hint="必须是 https —— 出站请求里带着密钥和收件人姓名电话地址"
        >
          <input
            aria-label="接口地址"
            value={form.baseUrl}
            onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
            className={`${INPUT} font-mono text-xs`}
          />
        </Field>

        <Field label="环境">
          <select
            aria-label="环境"
            value={form.environment}
            onChange={(e) => setForm({ ...form, environment: e.target.value })}
            className={INPUT}
          >
            <option value="SANDBOX">沙箱</option>
            <option value="PRODUCTION">生产</option>
          </select>
        </Field>

        <Field label="认证方式">
          <select
            aria-label="认证方式"
            value={form.authType}
            onChange={(e) => setForm({ ...form, authType: e.target.value })}
            className={INPUT}
          >
            {Object.entries(AUTH_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </Field>

        {form.authType !== 'NONE' && (
          <Field
            label="凭据引用"
            hint="★ 这里填的是环境变量名,不是密钥本身。密钥不进数据库,也就不会出现在备份和导出里"
          >
            <input
              aria-label="凭据引用"
              value={form.credentialRef}
              onChange={(e) => setForm({ ...form, credentialRef: e.target.value })}
              placeholder="env:CHANNEL_LALAMOVE"
              className={`${INPUT} font-mono text-xs`}
            />
          </Field>
        )}

        <Field label="静态常量" hint="模板里用 const.xxx 取,比如商户号">
          <textarea
            aria-label="静态常量"
            value={form.constants}
            onChange={(e) => setForm({ ...form, constants: e.target.value })}
            rows={2}
            spellCheck={false}
            className={`${INPUT} font-mono text-xs h-auto py-2`}
          />
        </Field>

        <Field label="通道配置" hint="模板里用 channel.config.xxx 取,比如回调地址">
          <textarea
            aria-label="通道配置"
            value={form.config}
            onChange={(e) => setForm({ ...form, config: e.target.value })}
            rows={2}
            spellCheck={false}
            className={`${INPUT} font-mono text-xs h-auto py-2`}
          />
        </Field>

        <div className="flex gap-3 pt-1">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300"
          >
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={busy || !form.name || (!existing && !form.code)}
            className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 能订阅的状态。
 *
 * 只列商家真的关心的那几个 —— 「待派单」「待接单」这种内部流转状态
 * 推给商家只是噪音,他关心的是「取了没有、到了没有、出没出问题」。
 */
const NOTIFY_STATUSES: Array<[string, string]> = [
  ['ASSIGNED', '已派单'],
  ['ACCEPTED', '骑手已接单'],
  ['PICKED_UP', '已取件'],
  ['IN_TRANSIT', '配送中'],
  ['DELIVERED', '已送达'],
  ['FAILED', '投递失败'],
  ['RETURNED', '已退回'],
  ['CANCELLED', '已取消'],
];

const INPUT =
  'w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}
